import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";
import { updateGOGroup } from "api/resources/go";
import { FormErrorProps } from "api/models/FormErrorProps";
import { ProtectedPaths } from "routes";

interface Client {
  id: number;
  name: string;
}

type EditGOGroupModalProps = {
  group: { id: number; name: string; clients: Client[] };
  dispatch: any;
  setEditModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const EditGOGroupModal = ({
  group,
  dispatch,
  setEditModal,
}: EditGOGroupModalProps) => {
  const [formErrors, setFormErrors] = useState(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const postData = {
      id: group.id,
      name: data.get("name"),
    };

    updateGOGroup(postData)
      .then(() => {
        setEditModal(false);
        dispatch();
      })
      .catch((error) => {
        setFormErrors(error.response.data.errors);
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Edit group
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item sm={12}>
            <TextField
              id="name"
              name="name"
              label="Name"
              size="small"
              required
              defaultValue={group.name}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item sm={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              Clients in this group ({group.clients?.length || 0})
            </Typography>
            <Divider />
            {group.clients?.length > 0 ? (
              <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                {group.clients.map((client) => (
                  <ListItem key={client.id} sx={{ px: 0.3 }}>
                    <ListItemText
                      primary={
                        <Link
                          href={`/${ProtectedPaths.Clients}/${client.id}`}
                          underline="hover"
                        >
                          {client.name}
                        </Link>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: grey[500], mt: 1, fontStyle: "italic" }}
              >
                No clients in this group
              </Typography>
            )}
          </Grid>
          {formErrors && (
            <Grid item sm={12}>
              {formErrors.map(({ message }: FormErrorProps) => (
                <Alert variant="filled" severity="error" sx={{ mb: "0.4rem" }}>
                  {message}
                </Alert>
              ))}
            </Grid>
          )}
          <Grid item sm={12} sx={{ mt: "1rem" }}>
            <Button variant="contained" type="submit" sx={{ mr: "1rem" }}>
              Save
            </Button>
            <Button onClick={() => setEditModal(false)}>Cancel</Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default EditGOGroupModal;
